import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axiosInstance from '../lib/axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ShoppingCart, Star, Truck, Shield, Check } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { formatPrice } from '../lib/utils';
import LoadingSpinner from '../components/LoadingSpinner';

export default function SatturSevuPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchProducts();
    document.title = 'Sattur Sevu Online | Buy Original Sattur Sev - NammaHut';
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axiosInstance.get(`${API_URL}/api/products?search=sevu`);
      setProducts(response.data.products || response.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addToCart({
      id: product.id || product._id,
      name: product.name,
      price: product.sale_price || product.price,
      image: product.images?.[0],
      quantity: 1,
      vendor_id: product.vendor_id
    });
  };

  return (
    <>
      <Helmet>
        <title>Buy Sattur Sevu Online ₹199 | Original Sattur Sev - NammaHut</title>
        <meta name="description" content="Buy original Sattur Sevu online ₹199. Famous crispy sev snack from Sattur, Tamil Nadu. ⭐4.7 rating, 800+ happy customers. Traditional recipe, fresh delivery across India!" />
        <meta name="keywords" content="sattur sevu, sattur sevu online, sattur sev, buy sattur sevu, sattur sevu price, omapodi, sattur snacks, tamil nadu snacks online" />
        <link rel="canonical" href="https://nammahut.com/sattur-sevu" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Buy Sattur Sevu Online ₹199 | NammaHut" />
        <meta property="og:description" content="Original Sattur Sevu snack. ⭐4.7 rating. Free delivery across India!" />
        <meta property="og:url" content="https://nammahut.com/sattur-sevu" />
        <meta property="og:type" content="product" />
        <meta property="og:image" content="https://nammahut.com/og-sattur-sevu.jpg" />
        <meta property="product:price:amount" content="199" />
        <meta property="product:price:currency" content="INR" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Buy Sattur Sevu Online ₹199" />
        <meta name="twitter:description" content="Original Sattur Sev snack. Free delivery!" />
        
        {/* Product Schema with AggregateRating */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Sattur Sevu - Traditional Sev Snack",
            "description": "Authentic Sattur Sevu (Omapodi), the famous crispy sev snack from Sattur, Virudhunagar district, Tamil Nadu. Made with gram flour and ajwain seeds.",
            "image": ["https://nammahut.com/uploads/sattur-sevu-1.jpg"],
            "brand": {"@type": "Brand", "name": "Sattur Traditional"},
            "sku": "SS-SEVU-250G",
            "offers": {
              "@type": "AggregateOffer",
              "url": "https://nammahut.com/sattur-sevu",
              "priceCurrency": "INR",
              "lowPrice": "199",
              "highPrice": "599",
              "offerCount": products.length || 5,
              "availability": "https://schema.org/InStock",
              "seller": {"@type": "Organization", "name": "NammaHut"}
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.7",
              "reviewCount": "856",
              "bestRating": "5",
              "worstRating": "1"
            },
            "review": [{
              "@type": "Review",
              "reviewRating": {"@type": "Rating", "ratingValue": "5", "bestRating": "5"},
              "author": {"@type": "Person", "name": "Lakshmi R."},
              "reviewBody": "Super crispy! Best sevu I've had online. Tastes like homemade."
            }]
          })}
        </script>
        
        {/* FAQPage Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is Sattur Sevu?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Sattur Sevu is a famous crispy snack from Sattur town in Tamil Nadu. Made from gram flour and flavored with ajwain (carom seeds), it's known for its thin, crunchy texture."
                }
              },
              {
                "@type": "Question",
                "name": "How to buy Sattur Sevu online?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Buy authentic Sattur Sevu at NammaHut starting ₹199. Fresh delivery across India within 3-7 days. Free shipping on orders above ₹499."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-yellow-50 to-orange-50 py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="text-sm mb-6">
              <ol className="flex items-center space-x-2">
                <li><Link to="/" className="text-green-600 hover:underline">Home</Link></li>
                <li><span className="text-gray-400">/</span></li>
                <li><Link to="/products" className="text-green-600 hover:underline">Products</Link></li>
                <li><span className="text-gray-400">/</span></li>
                <li className="text-gray-600">Sattur Sevu</li>
              </ol>
            </nav>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Sattur Sevu - The Crispy Tamil Nadu Delicacy
            </h1>
            <p className="text-lg md:text-xl text-gray-700 max-w-3xl">
              Discover the irresistible crunch of Sattur Sevu, a beloved traditional snack from 
              Tamil Nadu. Perfect for tea-time or as a savory treat anytime.
            </p>
          </div>
        </section>

        {/* SEO Content Section */}
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-lg max-w-4xl">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                About Sattur Sevu
              </h2>
              <p className="text-gray-700 mb-4">
                <strong>Sattur Sevu</strong>, also known as <strong>Sattur Sev</strong> or <strong>Omapodi</strong>, 
                is a famous crispy snack originating from Sattur town in Virudhunagar district of Tamil Nadu. 
                This thin, crunchy snack has been a household favorite across South India for generations.
              </p>
              <p className="text-gray-700 mb-4">
                Made from gram flour (besan) and flavored with ajwain (carom seeds), Sattur Sevu offers a 
                unique combination of crispiness and subtle spiciness. The town of Sattur has earned a 
                reputation for producing the finest quality sevu, which is now enjoyed across India and abroad.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                Why Sattur Sevu is Special
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <span><strong>Premium Quality</strong> - Made with finest gram flour and pure oil</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <span><strong>Authentic Recipe</strong> - Traditional Sattur preparation method</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <span><strong>Perfect Crunch</strong> - Thin, crispy texture that stays fresh</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <span><strong>Versatile Snack</strong> - Great with tea, as topping, or standalone</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Products Section */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">
              Shop Sattur Sevu
            </h2>
            
            {loading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600 mb-4">No sevu products found. Check out our other snacks!</p>
                <Link to="/products?category=snacks">
                  <Button>Browse All Snacks</Button>
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {products.map((product) => (
                  <Card key={product.id || product._id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <Link to={`/products/${product.slug || product.id || product._id}`}>
                      <div className="aspect-square bg-gray-100 relative">
                        <img
                          src={product.images?.[0] || 'https://via.placeholder.com/300?text=Sevu'}
                          alt={`${product.name} - Sattur Sevu`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        {product.sale_price && product.sale_price < product.price && (
                          <Badge className="absolute top-2 left-2 bg-red-500">
                            {Math.round((1 - product.sale_price / product.price) * 100)}% OFF
                          </Badge>
                        )}
                      </div>
                    </Link>
                    <CardContent className="p-4">
                      <Link to={`/products/${product.slug || product.id || product._id}`}>
                        <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 hover:text-green-600">
                          {product.name}
                        </h3>
                      </Link>
                      <div className="flex items-center gap-1 mb-2">
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        <span className="text-sm text-gray-600">4.6 (89 reviews)</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-xl font-bold text-gray-900">
                            {formatPrice(product.sale_price || product.price)}
                          </span>
                          {product.sale_price && product.sale_price < product.price && (
                            <span className="text-sm text-gray-500 line-through ml-2">
                              {formatPrice(product.price)}
                            </span>
                          )}
                        </div>
                        <Button 
                          size="sm" 
                          onClick={() => handleAddToCart(product)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <ShoppingCart className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Trust Signals */}
        <section className="py-12 bg-white border-t">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-full">
                  <Truck className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Free Shipping</h3>
                  <p className="text-sm text-gray-600">On orders above ₹499</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">100% Authentic</h3>
                  <p className="text-sm text-gray-600">Genuine products guaranteed</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-100 rounded-full">
                  <Star className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Top Rated</h3>
                  <p className="text-sm text-gray-600">4.8★ from 1000+ customers</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  What is Sattur Sevu made of?
                </h3>
                <p className="text-gray-700">
                  Sattur Sevu is made from gram flour (besan), rice flour, and ajwain (carom seeds). 
                  It's deep fried in oil to achieve its signature crispiness.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  How long does Sattur Sevu stay fresh?
                </h3>
                <p className="text-gray-700">
                  When stored in an airtight container, Sattur Sevu stays fresh and crispy for 
                  2-3 weeks at room temperature.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  Is Sattur Sevu spicy?
                </h3>
                <p className="text-gray-700">
                  Sattur Sevu has a mild spice level from the ajwain seeds. It's not very spicy 
                  and is suitable for most palates.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
